<template>
  <Dialog
    v-if="activeAuthor"
    v-model:visible="visible"
    modal
    :header="activeAuthorDisplayName"
    class="w-30rem"
  >
    <template #header>
      <div class="flex align-items-center gap-2">
        <Avatar
          v-if="activeAuthor?.thumbnail"
          :image="activeAuthor.thumbnail"
          shape="circle"
        />
        <i v-else class="pi pi-user"></i>
        <span class="font-bold">{{ activeAuthorDisplayName }}</span>
      </div>
    </template>
    <div v-html="activeAuthor?.bio"></div>
    <NuxtLink
      v-if="facultyMap?.[activeAuthor.id]"
      :to="facultyMap[activeAuthor.id].link"
    >
      <Button
        rounded
        label="Read More"
        class="border-none"
      />
    </NuxtLink>
  </Dialog>
  <div class="inline-flex flex-wrap align-items-center gap-2">
    <PersonAvatarGroup
      v-if="persons?.length"
      :people="persons as Author[]"
    />
    <span>
      <template
        v-for="(author, index) in persons as Author[]"
        :key="author.id"
      >
        <a
          v-if="canOpenAuthorDialog(author)"
          href="#"
          @click.prevent="openDialog(author)"
          class="text-color hover:underline"
        >
          {{ getPersonDisplayName(author.person_title, author.name) }}
        </a>
        <span v-else class="text-color">
          {{ getPersonDisplayName(author.person_title, author.name) }}
        </span>
        <span v-if="index < persons.length - 1">, </span>
      </template>
    </span>
  </div>
</template>

<script setup lang="ts">
import { getPersonDisplayName } from '~/composables/useUtils'

const props = defineProps({
  where: {
    type: Object as PropType<Record<string, any>>,
    required: true
  }
})

interface Author {
  id: number
  name: string
  person_title?: string
  bio?: string
  thumbnail?: string
}

interface Faculty {
  person_id: number
  link: string
}

const visible = ref(false)
const activeAuthor = ref<Author | null>(null)

const activeAuthorDisplayName = computed(() =>
  activeAuthor.value
    ? getPersonDisplayName(activeAuthor.value.person_title, activeAuthor.value.name)
    : ''
)

const { data: persons } = await useAsyncData<Author[]>(
  `${JSON.stringify(props.where)}-persons`,
  () => {
    return queryContent('person')
      .where(props.where)
      .only(['id', 'name', 'person_title', 'bio', 'thumbnail'])
      .find()
  }
)

const person_ids = computed(() => persons.value?.map((p: Author) => p.id) ?? [])

const { data: faculty } = await useLazyAsyncData<Faculty[]>(
  `${JSON.stringify(person_ids.value)}-faculty`,
  () => {
    if (!person_ids.value.length) return []

    return queryContent('staff')
      .where({
        person_id: { $in: person_ids.value }
      })
      .only(['person_id', 'link'])
      .find()
  }
)

const facultyMap = computed(() => {
  if (!faculty.value) return {}
  return Object.fromEntries(faculty.value.map((f: Faculty) => [f.person_id, f]))
})

function canOpenAuthorDialog(author: Author) {
  return Boolean(author.bio?.trim() || facultyMap.value[author.id])
}

function openDialog(author: Author) {
  if (!canOpenAuthorDialog(author)) return

  activeAuthor.value = author
  visible.value = true
}
</script>
